import { useCallback, useMemo, useEffect } from 'react'
import type { VFC } from 'react'
import type { CommonStoreState, License, SubLicense } from '../../store'
import { updateActivationInstances, type Actions } from '../../actions'
import type { BannerProps } from './UAccordion'
import UAccordionHeader from './UAccordionHeader'
import { APP_ID_SCM } from '../common/GlobalConstants'
import UTextBox from './UTextBox'
import { Checkbox } from '@panwds/react-form'
import { useFormContext } from 'react-hook-form'
import { ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP, ACTIVATION_UI_ERROR_CODES } from '../../ErrorCodes'
import { StringTemplate } from '../common/utils'

const defaultProps = {
  title: 'Strata Cloud Manager License',
  subtitle: 'If you plan on adding more tenants or subtenants after activation, only assign what\'s needed for this tenant.'
}
const SCM_FIELD_NAME = 'scm'

type UDeviceLicenseProps = {
  state: CommonStoreState
  actions: Actions
  title?: string
  subtitle?: string
  updateTitle: (title: string) => void
  setDisable: (disable: boolean) => void
  setBanner?: (props: BannerProps) => void
  setAccordionOpen: (isOpen: boolean) => void
  completeAccordion: () => void
}
const UDeviceLicense: VFC<UDeviceLicenseProps> = (props) => {
  const { state, title, subtitle, actions, setBanner, setDisable, updateTitle, setAccordionOpen, completeAccordion, ...restProps } = props
  const { licenses, instances, loadingTsg } = state
  const { setError, clearErrors } = useFormContext()
  const [deviceLicense, deviceSubLicense] = useMemo<[License | undefined, SubLicense | undefined]>(() => {
    const _deviceLicense = licenses?.get(APP_ID_SCM)
    return [_deviceLicense, _deviceLicense?.licenses[0]]
  }, [licenses])

  const writeSCMInstToActivationData = useCallback(() => {
    const hasSCMnstance = instances?.get(APP_ID_SCM)
    updateActivationInstances({
      [APP_ID_SCM]: {
        app_id: APP_ID_SCM,
        activation_type: hasSCMnstance ? 'AMEND' : 'NEW',
        allocation_type: 'FULL',
        allocation: [
          {
            app_id: APP_ID_SCM,
            license_type: deviceSubLicense?.license_type,
            allocation_size: deviceSubLicense?.purchased_size ? +deviceSubLicense?.purchased_size : 0
          }
        ]
      }
    })
  }, [deviceSubLicense?.purchased_size, deviceSubLicense?.license_type, instances])

  useEffect(() => {
    writeSCMInstToActivationData()
  }, [deviceLicense, writeSCMInstToActivationData])

  useEffect(() => {
    if (!loadingTsg) {
      const scmInstance = instances?.get(APP_ID_SCM)
      if (scmInstance && scmInstance?.entitlements?.length > 0) {
        setError(SCM_FIELD_NAME, { message: StringTemplate(ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.INSTANCE_ALREADY_EXISTS], ['Strata Cloud Manager Premium']) })
      }
      else {
        clearErrors(SCM_FIELD_NAME)
      }
    }
    else {
      clearErrors(SCM_FIELD_NAME)
    }

  }, [loadingTsg, instances, setError, clearErrors])
  return (
    <>
      <div>
        <UAccordionHeader title={title || defaultProps.title} subtitle={subtitle || defaultProps.subtitle} />
        <div className='cm-flex cm-flex-row'>
          <div className='cm-flex-1'>
            Licenses
          </div>
        </div>
        <div className='cm-flex cm-flex-row'>
          <div className='cm-flex-1 cm-w-100'>
            <div className='cm-flex cm-flex-row'>
              <div>
                <Checkbox
                  disabled={true}
                  name='scm-chk'
                  label='Strata Cloud Manager Premium'
                  showLabel={false}
                  defaultValue={true as any}>
                </Checkbox>
              </div>
              <div className='cm-pt-1'>
                <span>Strata Cloud Manager Premium</span>
              </div>
            </div>
            <div className='cm-flex cm-flex-row cm-pl-3'>
              <div>
                <Checkbox
                  disabled={true}
                  name='sls-chk'
                  label='Strata Logging Service'
                  showLabel={false}
                  defaultValue={true as any}>
                </Checkbox>
              </div>
              <div className='cm-pt-1'>
                <span>Strata Logging Service</span><br />
                <span className="cm-flex tw-text-xs tw-font-normal tw-leading-5 tw-font-lato tw-items-center tw-space-x-2 tw-cursor-not-allowed tw-text-gray-400">
                  <span className="">Includes 1 year of log retention</span>
                </span>
              </div>
            </div>
          </div>
          <div className=' cm-flex-none cm-w-30 cm-pt-1'>
            <span> Devices</span>
          </div>
          <div className=' cm-flex-1'>

            <UTextBox {...{
              value: deviceSubLicense?.purchased_size?.toString(),
              disabled: true,
              placeholder: 'Enter SCM Quantity',
              name: 'scmdev',
              showLabel: false,
              label: '',
              subTitle: `Up to ${deviceSubLicense?.purchased_size ?? 0} available`,
              errorMessage: 'SCM_QUANTITY_INPUT_ERROR',
              height: '32px',
              width: '50px'
            }} />
          </div>
        </div>
      </div >
    </>
  )
}
export default UDeviceLicense
